import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, CheckCircle } from '@phosphor-icons/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import notify, { notifyIcons } from '../utils/notify';
import { getGiveawayBySlug, participateInGiveaway } from '../services/api';
import telegramService from '../services/telegram';
import ContactFooter from '../components/ContactFooter';
import MarkdownLink from '../components/MarkdownLink';
import { plural } from '../utils/plural';
import { useAuth } from '../context/AuthContext';

const PRIZES = ['приз', 'приза', 'призов'];
const DAYS = ['день', 'дня', 'дней'];
const HOURS = ['час', 'часа', 'часов'];
const MINUTES = ['минута', 'минуты', 'минут'];

/**
 * Дата подведения итогов — та же `endDate`, из которой считается отсчёт.
 * Отдельного поля в схеме розыгрыша нет (решение заказчика 30.07.2026:
 * итоги подводятся по окончании приёма заявок, второе поле не заводим).
 * Так же её называет и список розыгрышей в профиле.
 */
const formatResultsDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  // Intl склоняет верно, когда в наборе есть день: «12 августа»
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
};

/**
 * Остаток времени крупной единицей, а не «14д 2ч 29м».
 *
 * Мелкие разряды в отсчёте на две недели ничего не решают, зато меняются
 * каждую минуту — строка дёргалась даже с табличными цифрами. Минуты
 * появляются только в последний час, когда они действительно важны.
 */
const formatTimeLeft = (endDate) => {
  const diff = new Date(endDate) - Date.now();
  if (Number.isNaN(diff)) return null;
  if (diff <= 0) return { ended: true, label: 'Розыгрыш завершён' };

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return { ended: false, label: `Осталось ${days} ${plural(days, DAYS)}` };
  if (hours > 0) return { ended: false, label: `Осталось ${hours} ${plural(hours, HOURS)}` };
  return { ended: false, label: `Осталось ${minutes} ${plural(minutes, MINUTES)}` };
};

const GiveawayPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [giveaway, setGiveaway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isParticipating, setIsParticipating] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  const loadGiveaway = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getGiveawayBySlug(slug);

      if (response.data) {
        setGiveaway(response.data);
        setIsParticipating(response.data.isParticipant || false);
      } else {
        setError('Розыгрыш не найден');
      }
    } catch (err) {
      setError('Не удалось загрузить розыгрыш');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadGiveaway();
  }, [loadGiveaway]);

  // Пересчёт раз в минуту, а не в секунду: крупная единица чаще не меняется
  useEffect(() => {
    if (!giveaway?.endDate) return undefined;

    setTimeLeft(formatTimeLeft(giveaway.endDate));
    const timer = setInterval(() => setTimeLeft(formatTimeLeft(giveaway.endDate)), 60000);
    return () => clearInterval(timer);
  }, [giveaway?.endDate]);

  const isEnded = Boolean(timeLeft?.ended);

  const handleParticipate = async () => {
    if (!giveaway) return;

    try {
      telegramService.hapticFeedback('medium');

      const response = await participateInGiveaway(giveaway.documentId || giveaway.slug);

      if (response.data.success) {
        setIsParticipating(true);
        // Завершение чего-то крупного — один из двух случаев заливки (брендбук 8.2)
        notify.success('Вы участвуете в розыгрыше');
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setIsParticipating(true);
        notify('Вы уже участвуете в розыгрыше', { icon: notifyIcons.info });
      } else if (err.response?.status === 401) {
        notify.error('Войдите, чтобы участвовать');
      } else if (err.response?.status === 403) {
        notify.error('Участие в этом розыгрыше недоступно');
      } else {
        notify.error('Не удалось отправить заявку. Попробуйте позже');
      }
    }
  };

  const getImageUrl = (image) => {
    if (!image) return '';
    if (image.url?.startsWith('http')) return image.url;
    return `${window.location.origin}${image.url || ''}`;
  };

  const getUserAvatar = (winner) => {
    if (!winner) return '';
    if (winner.avatar?.url) {
      if (winner.avatar.url.startsWith('http')) return winner.avatar.url;
      return `${window.location.origin}${winner.avatar.url}`;
    }
    return winner.photoUrl || '';
  };

  /**
   * `username` здесь не читается: выборка победителей в контроллере
   * (`fields: ['id', 'firstName', 'telegramUsername', 'photoUrl']`) его
   * не отдаёт — ветка была мёртвой и скрытой тем, что `firstName` идёт
   * первым. У кого имени нет, показывается @-ник.
   */
  const getWinnerName = (winner) => {
    if (winner.firstName) return winner.firstName;
    if (winner.telegramUsername) return `@${winner.telegramUsername}`;
    return 'Участник';
  };

  // Скелетоны вместо спиннера: каркас держит ту же разметку, в которую
  // страница развернётся, и экран не схлопывается в точку по центру
  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg pb-6">
        <div
          className="px-4 pb-6"
          style={{ paddingTop: 'calc(var(--safe-area-top) + var(--tg-header-height) + 16px)' }}
        >
          <div className="animate-shimmer mb-4 h-56 w-full rounded-lg" />
          <div className="h-7 w-3/4 rounded-sm bg-theme-card-secondary" />
          <div className="mt-2 h-4 w-2/5 rounded-sm bg-theme-card-secondary" />
          <div className="mt-4 h-12 w-full rounded-md bg-theme-card-secondary" />
          <div className="mt-6 h-4 w-full rounded-sm bg-theme-card-secondary" />
          <div className="mt-2 h-4 w-5/6 rounded-sm bg-theme-card-secondary" />
        </div>
      </div>
    );
  }

  if (error || !giveaway) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-theme-bg px-4">
        <div className="text-center">
          <p className="mb-6 text-body text-theme-text-secondary">
            {error || 'Розыгрыш не найден'}
          </p>
          <button
            onClick={() => {
              telegramService.hapticFeedback('light');
              navigate('/');
            }}
            className="h-12 rounded-md border-0 bg-theme-card-secondary px-[18px] text-label text-action-text transition-colors duration-fast ease-out active:scale-98"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            На главную
          </button>
        </div>
      </div>
    );
  }

  const imageUrl = getImageUrl(giveaway.image);
  const resultsDate = formatResultsDate(giveaway.endDate);
  const prizesCount = giveaway.winnersCount || 0;
  // Дата итогов и число призов — то, что помогает решить, участвовать ли.
  // Число участников намеренно не показываем (решение заказчика)
  const meta = [
    resultsDate && `Итоги ${resultsDate}`,
    prizesCount > 0 && `${prizesCount} ${plural(prizesCount, PRIZES)}`,
  ].filter(Boolean);

  const winners = giveaway.winners || [];

  return (
    <div className="min-h-screen bg-theme-bg pb-6">
      <div
        className="px-4 pb-6"
        style={{ paddingTop: 'calc(var(--safe-area-top) + var(--tg-header-height) + 16px)' }}
      >
        {/* Фото 224px вместо жёстких 400: на узком экране прежняя высота
            занимала его почти целиком, и название уезжало за кромку */}
        {imageUrl && (
          <div className="mb-4 h-56 w-full overflow-hidden rounded-lg">
            <img src={imageUrl} alt={giveaway.title} className="h-full w-full object-cover" />
          </div>
        )}

        <h1 className="text-h1 text-theme-text-primary">{giveaway.title}</h1>

        {meta.length > 0 && (
          <p className="tabular mt-2 text-body-sm text-theme-text-secondary">
            {meta.join(' · ')}
          </p>
        )}

        {/* Завершение — нейтральным цветом: красный по правилу 2.1 означает
            опасность и ошибку, а окончание розыгрыша это просто факт */}
        {timeLeft && (
          <p
            className={`tabular mt-2 flex items-center gap-2 text-body-sm ${
              isEnded ? 'text-theme-text-secondary' : 'font-semibold text-action-text'
            }`}
          >
            <Clock size={18} className="flex-shrink-0" />
            {timeLeft.label}
          </p>
        )}

        {/* Кнопка 48px вместо 56: она была крупнее названия — человек решал
            участвовать раньше, чем понимал, что разыгрывают */}
        {giveaway.requireParticipation && !isEnded && !isParticipating && (
          <button
            onClick={handleParticipate}
            className="mt-4 h-12 w-full rounded-md bg-action text-label text-on-action transition-colors duration-fast ease-out active:scale-98"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            Участвовать
          </button>
        )}

        {/* Участие — строка-статус, а не кнопка: нажать её нельзя, значит
            и выглядеть нажимаемой она не должна. Зелёный уходит по правилу
            2.1 — участие это состояние, а не достигнутый результат */}
        {giveaway.requireParticipation && isParticipating && (
          <div
            className="mt-4 flex items-center gap-2 px-4 py-3"
            style={{
              background: 'var(--color-card-secondary)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <CheckCircle
              size={20}
              weight="fill"
              className="flex-shrink-0 text-theme-text-secondary"
            />
            {/* Прошедшее время после завершения: «Вы участвуете» на закрытом
                розыгрыше обещает то, чего уже не происходит. Дата итогов тут
                же не повторяется — она стоит строкой выше, а на завершённом
                розыгрыше отсчёт её и так не заменяет */}
            <span className="text-label text-theme-text-primary">
              {isEnded ? 'Вы участвовали' : 'Вы участвуете'}
            </span>
            {!isEnded && resultsDate && (
              <span className="tabular ml-auto text-caption text-theme-text-secondary">
                итоги {resultsDate}
              </span>
            )}
          </div>
        )}

        {/* Описание основным цветом текста, как на странице маршрута:
            серым набирают подписи, а не сам текст (брендбук 3.4) */}
        {giveaway.description && (
          <div className="mt-6 text-body text-theme-text-primary">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                /* Содержимое приходит из markdown через {...props},
                   правило этого не видит */
                /* eslint-disable jsx-a11y/heading-has-content */
                h1: ({ node, ...props }) => <h1 className="mb-2 mt-4 text-h2" {...props} />,
                h2: ({ node, ...props }) => <h2 className="mb-2 mt-4 text-h2" {...props} />,
                h3: ({ node, ...props }) => <h3 className="mb-2 mt-4 text-h3" {...props} />,
                /* eslint-enable jsx-a11y/heading-has-content */
                p: ({ node, ...props }) => <p className="mb-3 last:mb-0" {...props} />,
                ul: ({ node, ...props }) => (
                  <ul className="mb-3 list-inside list-disc last:mb-0" {...props} />
                ),
                ol: ({ node, ...props }) => (
                  <ol className="mb-3 list-inside list-decimal last:mb-0" {...props} />
                ),
                li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                // Общий компонент: ссылка на наш домен открывается внутри
                // приложения, внешняя — через Telegram. Прежняя проверка
                // ловила только текущий origin, поэтому абсолютная ссылка
                // на appnefspb.ru уходила в браузер
                a: MarkdownLink,
              }}
            >
              {DOMPurify.sanitize(giveaway.description)}
            </ReactMarkdown>
          </div>
        )}

        {winners.length > 0 && (
          <div className="mt-8">
            <h2 className="text-h2 text-theme-text-primary">Победители</h2>

            <div className="mt-4 grid grid-cols-3 gap-2">
              {winners.map((winner) => {
                // Своя карточка выделена рамкой 2px и подложкой — без
                // текстовой метки: она дублировала бы то, что уже сказала
                // форма. Толщина рамки читается и без различения оттенков (2.5)
                const isMe = Boolean(user?.id) && winner.id === user.id;
                const avatarUrl = getUserAvatar(winner);
                const name = getWinnerName(winner);

                return (
                  <div
                    key={winner.id}
                    className="flex flex-col items-center px-2 py-4 text-center"
                    style={{
                      background: isMe
                        ? 'var(--color-success-dim)'
                        : 'var(--color-card)',
                      border: isMe
                        ? '2px solid var(--color-success)'
                        : '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt=""
                        className="mb-2 block object-cover"
                        style={{ width: 52, height: 52, borderRadius: 'var(--radius-full)' }}
                      />
                    ) : (
                      /* Сине-фиолетовый градиент убран — самый узнаваемый
                         признак сгенерированного дизайна (антипаттерны 15.1) */
                      <div
                        className="mb-2 flex items-center justify-center"
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: 'var(--radius-full)',
                          background: 'var(--color-action)',
                        }}
                      >
                        <span className="font-display text-h3 text-on-action">
                          {name.replace('@', '')[0]?.toUpperCase() || 'У'}
                        </span>
                      </div>
                    )}

                    <p
                      className={`w-full truncate text-body-sm ${
                        isMe ? 'font-bold' : 'font-semibold'
                      } text-theme-text-primary`}
                      title={name}
                    >
                      {name}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="px-4">
        <ContactFooter />
      </div>
    </div>
  );
};

export default GiveawayPage;
